import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  BellRing,
  CalendarClock,
  CheckCircle2,
  CircleAlert,
  Megaphone,
  MessageSquareWarning,
  RotateCcw,
  Send,
  ShieldAlert,
  Sparkles,
  UserRound,
  Wrench,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';

import Dropdown from '../../components/Dropdown';
import FloatingField from '../../components/FloatingField';
import { API_URL } from '../../config';
import useToast from '../../hooks/useToast';
import { safeFetch } from '../../utils/safeFetch';
import { formatPersonName } from '../../utils/personName';
import { haptic } from '../../utils/vibration';

const TITLE_LIMIT = 90;
const MESSAGE_LIMIT = 320;

const TYPE_OPTIONS = [
  { value: 'info', label: 'Информация', icon: CircleAlert },
  { value: 'success', label: 'Успех', icon: CheckCircle2 },
  { value: 'warning', label: 'Внимание', icon: AlertTriangle },
  { value: 'error', label: 'Ошибка', icon: ShieldAlert },
];

const TYPE_STYLES = {
  info: {
    badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
    ring: 'ring-blue-100 dark:ring-blue-900/40',
    icon: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  success: {
    badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
    ring: 'ring-emerald-100 dark:ring-emerald-900/40',
    icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  warning: {
    badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
    ring: 'ring-amber-100 dark:ring-amber-900/40',
    icon: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  },
  error: {
    badge: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300',
    ring: 'ring-red-100 dark:ring-red-900/40',
    icon: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
};

const TEMPLATE_OPTIONS = [
  {
    id: 'new-order',
    icon: BellRing,
    notificationType: 'success',
    title: 'Новый заказ',
    message: 'По вашему направлению появился новый заказ. Проверьте карточку и приоритет выполнения.',
    description: 'Сообщить о новом заказе',
  },
  {
    id: 'urgent-order',
    icon: AlertTriangle,
    notificationType: 'warning',
    title: 'Срочный заказ',
    message: 'Заказ помечен как срочный. Проверьте очередь и возьмите его в приоритетную обработку.',
    description: 'Подсветить срочность',
  },
  {
    id: 'schedule-changed',
    icon: CalendarClock,
    notificationType: 'info',
    title: 'Изменение расписания',
    message: 'По одному из заказов изменились дата или интервал. Проверьте детали в карточке заказа.',
    description: 'Напомнить про смену времени',
  },
  {
    id: 'order-ready',
    icon: CheckCircle2,
    notificationType: 'success',
    title: 'Заказ готов',
    message: 'Заказ готов к следующему этапу. Проверьте детали и завершите нужные действия без задержки.',
    description: 'Сообщить о готовности',
  },
  {
    id: 'comment-needed',
    icon: MessageSquareWarning,
    notificationType: 'warning',
    title: 'Нужно уточнение',
    message: 'По заказу требуется комментарий или уточнение. Откройте карточку и проверьте детали.',
    description: 'Попросить быстро отреагировать',
  },
  {
    id: 'photo-reminder',
    icon: Sparkles,
    notificationType: 'info',
    title: 'Напоминание по фото',
    message: 'Не забудьте загрузить фото или закрывающие материалы по своему этапу, если они нужны.',
    description: 'Напомнить про вложения',
  },
  {
    id: 'service-notice',
    icon: Megaphone,
    notificationType: 'info',
    title: 'Служебное сообщение',
    message: 'Проверьте внутреннюю информацию по смене. Сообщение требует внимания, но не срочного ответа.',
    description: 'Обычное служебное уведомление',
  },
  {
    id: 'technical',
    icon: Wrench,
    notificationType: 'error',
    title: 'Техническое ограничение',
    message: 'Есть техническое ограничение или сбой. Приостановите действие и дождитесь уточнения от старшего.',
    description: 'Сообщить о проблеме',
  },
];

const USER_TYPE_LABELS = {
  user: 'Сотрудник',
  admin: 'Администратор',
  developer: 'Разработчик',
  dispatcher: 'Диспетчер',
};

const getTypeMeta = (value) => TYPE_OPTIONS.find((option) => option.value === value) || TYPE_OPTIONS[0];

const getShortPersonName = (user) => {
  if (!user) return 'Без имени';
  const lastName = String(user.last_name || '').trim();
  const firstName = String(user.first_name || '').trim();
  if (lastName && firstName) {
    return `${lastName} ${firstName}`;
  }
  return formatPersonName(user, 'Без имени');
};

const buildUserLabel = (user) => {
  const roleLabel = USER_TYPE_LABELS[user.user_type] || 'Пользователь';
  const baseName = getShortPersonName(user);
  return `${baseName} · ${roleLabel}`;
};

const NotificationsSkeleton = () => (
  <div className="space-y-3 animate-fade-in">
    <div className="grid gap-3 xl:grid-cols-[minmax(0,1.1fr)_320px]">
      <div className="space-y-3 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900">
        <div className="h-6 w-48 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        <div className="grid gap-3 md:grid-cols-2">
          <div className="h-12 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
          <div className="h-12 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
        </div>
        <div className="h-12 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
        <div className="h-36 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
        <div className="flex justify-end gap-2">
          <div className="h-11 w-28 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
          <div className="h-11 w-36 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
      <div className="space-y-3 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900">
        <div className="h-6 w-32 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        <div className="h-40 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
      </div>
    </div>
    <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900">
      <div className="mb-3 h-6 w-28 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        {[...Array(8)].map((_, index) => (
          <div key={index} className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
        ))}
      </div>
    </div>
  </div>
);

const NotificationsManagement = () => {
  const { toast, showToast, hideToast } = useToast(3200);
  const userType = localStorage.getItem('user_type');

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState('info');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const data = await safeFetch(`${API_URL}/api/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        const rawUsers = Array.isArray(data) ? data : Array.isArray(data?.users) ? data.users : [];
        if (cancelled) return;

        const nextUsers = rawUsers
          .filter((user) => user && user.user_status !== 'deleted')
          .sort((left, right) => buildUserLabel(left).localeCompare(buildUserLabel(right), 'ru'));

        setUsers(nextUsers);
      } catch (error) {
        if (cancelled) return;
        haptic('error');
        showToast(error.message || 'Не удалось загрузить список пользователей', 'error');
        setUsers([]);
      } finally {
        if (!cancelled) {
          setLoadingUsers(false);
        }
      }
    };

    fetchUsers();

    return () => {
      cancelled = true;
    };
  }, [showToast]);

  if (userType !== 'admin' && userType !== 'developer') {
    return <Navigate to="/" replace />;
  }

  const userOptions = users.map((user) => ({
    value: String(user.id),
    label: buildUserLabel(user),
  }));

  const selectedUserMeta = users.find((user) => String(user.id) === String(selectedUser));
  const typeMeta = getTypeMeta(notificationType);
  const typeStyles = TYPE_STYLES[notificationType] || TYPE_STYLES.info;
  const TypeIcon = typeMeta.icon;

  const remainingTitle = TITLE_LIMIT - title.length;
  const remainingMessage = MESSAGE_LIMIT - message.length;

  const resetForm = () => {
    haptic('close');
    setSelectedUser('');
    setTitle('');
    setMessage('');
    setNotificationType('info');
  };

  const applyTemplate = (template) => {
    haptic('selection');
    setTitle(template.title);
    setMessage(template.message);
    setNotificationType(template.notificationType);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedMessage = message.trim();

    if (!selectedUser) {
      haptic('warning');
      showToast('Выберите пользователя', 'error');
      return;
    }

    if (!trimmedTitle || !trimmedMessage) {
      haptic('warning');
      showToast('Заполните заголовок и текст уведомления', 'error');
      return;
    }

    setSending(true);

    try {
      await safeFetch(`${API_URL}/api/notifications`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: Number(selectedUser),
          title: trimmedTitle,
          message: trimmedMessage,
          notification_type: notificationType,
        }),
      });

      haptic('success');
      showToast('Уведомление отправлено', 'success');
      setTitle('');
      setMessage('');
      setNotificationType('info');
    } catch (error) {
      haptic('error');
      showToast(error.message || 'Не удалось отправить уведомление', 'error');
    } finally {
      setSending(false);
    }
  };

  if (loadingUsers && !users.length) {
    return (
      <>
        <NotificationsSkeleton />
              </>
    );
  }

  return (
    <div className="space-y-3 animate-fade-in desktop-content-container-wide">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.1fr)_320px]">
        <form onSubmit={handleSubmit} className="space-y-4 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 animate-slide-up">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Новое уведомление</div>
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Если у сотрудника включены push-уведомления, сообщение уйдёт и туда.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                {users.length} пользователей
              </span>
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <RotateCcw className="h-4 w-4" />
                Сбросить
              </button>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Dropdown
                label="Получатель"
                value={selectedUser}
                onChange={(value) => {
                  haptic('selection');
                  setSelectedUser(value);
                }}
                options={userOptions}
                placeholder={users.length ? 'Выберите пользователя' : 'Нет доступных пользователей'}
                disabled={sending || !users.length}
                menuLabel="Пользователи"
                buttonClassName="rounded-xl py-3"
                menuClassName="z-30"
              />
            </div>

            <div>
              <Dropdown
                label="Тип уведомления"
                value={notificationType}
                onChange={(value) => {
                  haptic('selection');
                  setNotificationType(value);
                }}
                options={TYPE_OPTIONS}
                placeholder="Выберите тип"
                disabled={sending}
                buttonClassName="rounded-xl py-3"
                menuClassName="z-30"
              />
            </div>
          </div>

          <div className="block">
            <div className="mb-2 flex items-center justify-end gap-3">
              <span className={`text-xs ${remainingTitle < 10 ? 'text-amber-600 dark:text-amber-300' : 'text-gray-400 dark:text-gray-500'}`}>
                {title.length}/{TITLE_LIMIT}
              </span>
            </div>
            <FloatingField label="Заголовок">
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={TITLE_LIMIT}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                placeholder="Например: Срочный заказ"
                required
              />
            </FloatingField>
          </div>

          <div className="block">
            <div className="mb-2 flex items-center justify-end gap-3">
              <span className={`text-xs ${remainingMessage < 20 ? 'text-amber-600 dark:text-amber-300' : 'text-gray-400 dark:text-gray-500'}`}>
                {message.length}/{MESSAGE_LIMIT}
              </span>
            </div>
            <FloatingField label="Текст">
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={5}
                maxLength={MESSAGE_LIMIT}
                className="min-h-[136px] w-full resize-none rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                placeholder="Короткий и понятный текст уведомления"
                required
              />
            </FloatingField>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
            <button
              type="submit"
              disabled={sending || loadingUsers || !users.length}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {sending ? 'Отправка...' : 'Отправить'}
            </button>
          </div>
        </form>

        <div className="space-y-3 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 animate-slide-up">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Предпросмотр</div>
            <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium ring-1 ${typeStyles.badge} ${typeStyles.ring}`}>
              <TypeIcon className="h-3.5 w-3.5" />
              {typeMeta.label}
            </span>
          </div>

          <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-800">
            <div className="flex items-start gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${typeStyles.icon}`}>
                <BellRing className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {title.trim() || 'Заголовок уведомления'}
                  </div>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">сейчас</span>
                </div>
                <div className="mt-1 break-words text-sm leading-5 text-gray-600 dark:text-gray-300">
                  {message.trim() || 'Текст уведомления появится здесь.'}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2 rounded-xl border border-gray-200 p-3 dark:border-gray-800">
            <div className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Получатель</div>
            <div className="flex w-full min-w-0 items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
              <UserRound className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
              <span className="min-w-0 truncate">
                {selectedUserMeta ? buildUserLabel(selectedUserMeta) : 'Пользователь не выбран'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 animate-slide-up">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Шаблоны</div>
          <div className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            {TEMPLATE_OPTIONS.length}
          </div>
        </div>

        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
          {TEMPLATE_OPTIONS.map((template) => {
            const TemplateIcon = template.icon;
            const templateTypeMeta = getTypeMeta(template.notificationType);
            const templateTypeStyles = TYPE_STYLES[template.notificationType] || TYPE_STYLES.info;

            return (
              <button
                key={template.id}
                type="button"
                onClick={() => applyTemplate(template)}
                className="group flex min-h-[100px] items-start gap-3 rounded-xl border border-gray-200 p-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/60"
              >
                <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${templateTypeStyles.icon}`}>
                  <TemplateIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="break-words text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {template.title}
                    </div>
                    <span className={`rounded-lg px-2 py-1 text-[10px] font-medium ${templateTypeStyles.badge}`}>
                      {templateTypeMeta.label}
                    </span>
                  </div>
                  <div className="mt-1 line-clamp-2 break-words text-xs leading-5 text-gray-500 dark:text-gray-400">
                    {template.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

          </div>
  );
};

export default NotificationsManagement;
